import React, { useEffect, useState } from 'react';
import { ShieldCheck, ShieldAlert, RotateCcw } from 'lucide-react';
import { Badge } from '../ui-v2.5';
import { toast } from 'react-toastify';
import {
  fetchSegundoFactorDePersona,
  fetchRestablecerSegundoFactor,
} from '../../../peticiones_API/PeticionesSeguridad';

/**
 * Fase A1 · el bloque de Acceso y seguridad de la ficha.
 *
 * **Este bloque lo CREA A1 y lo amplía la Fase 12**, que meterá aquí el IBAN y el número de la
 * Seguridad Social descifrados, tras la misma puerta. Por eso se deja como una lista de filas a
 * la que se añaden elementos, no como una tarjeta con dos datos incrustados: añadirle campos no
 * debe obligar a rehacerlo.
 *
 * No enseña ningún secreto: dice si la persona tiene el segundo factor, no cuál es.
 */
export function BloqueAccesoSeguridad({ personaId, tipo, puedeRestablecer }) {
  const [estado, setEstado] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [restableciendo, setRestableciendo] = useState(false);

  const cargar = () => {
    if (!personaId) return;
    setCargando(true);
    fetchSegundoFactorDePersona(personaId, tipo)
      .then(setEstado)
      .catch(() => setEstado(null))
      .finally(() => setCargando(false));
  };

  useEffect(cargar, [personaId, tipo]);   // eslint-disable-line react-hooks/exhaustive-deps

  const restablecer = async () => {
    if (restableciendo) return;
    setRestableciendo(true);
    try {
      const r = await fetchRestablecerSegundoFactor(personaId, tipo);
      toast.success(r.message || 'Verificación en dos pasos retirada');
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'No se ha podido retirar');
    } finally {
      setRestableciendo(false);
    }
  };

  if (cargando) return <p className="text-[13px] text-v25-ink-500">Comprobando acceso y seguridad…</p>;
  if (!estado) return null;

  const activo = estado.configurado && estado.verificado;

  return (
    <section className="rounded-xl border border-v25-line p-4">
      <h3 className="mb-3 flex items-center gap-2 text-[14px] font-semibold">
        {activo ? <ShieldCheck size={18} /> : <ShieldAlert size={18} />}
        Acceso y seguridad
      </h3>

      <dl className="space-y-2 text-[13px]">
        <div className="flex items-center justify-between gap-3">
          <dt>Verificación en dos pasos</dt>
          <dd>
            {activo
              ? <Badge tone="success">Activada y verificada</Badge>
              : estado.configurado
                ? <Badge tone="warning">Empezada, sin verificar</Badge>
                : <Badge tone="neutral">Sin configurar</Badge>}
          </dd>
        </div>

        {activo && (
          <div className="flex items-center justify-between gap-3">
            <dt>Códigos de respaldo sin usar</dt>
            <dd className={estado.respaldosSinUsar === 0 ? 'text-v25-expired-fg' : ''}>
              {estado.respaldosSinUsar}
              {estado.respaldosSinUsar === 0 && ' · no podrá entrar si pierde el móvil'}
            </dd>
          </div>
        )}

        {/* `desde` a NULL significa una cosa concreta —se configuro antes de que esto se
            anotara— y se dice con palabras, no con un guion. Un hueco y un "no se sabe" se leen
            distinto: el primero parece un fallo de carga. */}
        {estado.configurado && (
          <div className="flex items-center justify-between gap-3">
            <dt>Activada desde</dt>
            <dd className={estado.desde ? '' : 'text-v25-ink-500'}>
              {estado.desde
                ? new Date(estado.desde).toLocaleDateString('es-ES',
                    { day: '2-digit', month: 'long', year: 'numeric' })
                : 'Antes de que se registrara'}
            </dd>
          </div>
        )}
      </dl>

      {!estado.configurado && (
        <p className="mt-3 text-[12px] text-v25-ink-500">
          Sin la verificación en dos pasos, esta persona no puede consultar datos sensibles: ni la
          pestaña Soporte ni sus propias contraseñas. La configura ella misma desde su perfil.
        </p>
      )}

      {puedeRestablecer && estado.configurado && (
        <div className="mt-3 border-t border-v25-line pt-3">
          <button type="button" onClick={restablecer} disabled={restableciendo}
            className="inline-flex items-center gap-2 rounded-lg border border-v25-line px-3 py-2 text-[13px] disabled:opacity-50">
            <RotateCcw size={15} />
            {restableciendo ? 'Retirando…' : 'Restablecer la verificación en dos pasos'}
          </button>
          <p className="mt-2 text-[12px] text-v25-ink-500">
            La retira para que pueda volver a configurarla — es lo que hace falta cuando alguien
            pierde el móvil. No se la configura nadie por ella.
          </p>
        </div>
      )}
    </section>
  );
}
